import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import firebase from "firebase/app";
import { toast } from "react-toastify";
import capitalize from "lodash/capitalize";
import { useSelector, useStore } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import {
  bankAccountByIdSelector,
  nonAssociatedIntegrationsSelector,
  bankAccountsSelector,
} from "src/store/bankAccount/selector";
import { integrationByIdSelector } from "src/store/plaidIntegration/selector";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import { addBankAccount } from "src/apiService/modules/bank-accounts";
import { addGeneralLedgerAccount } from "src/apiService/modules/generalLedgerAccounts";
import type {
  BankAccount,
  BankAccountType,
} from "src/interfaces/bank-account.interface";
import LoadableButton from "src/components/LoadableButton";
import ConfirmDialog from "src/components/ConfirmDialog";
import firestore from "src/apiService/firestore";
import FirebaseBatcher from "src/utils/FirebaseBatcher";
import { getAllSales } from "src/store/sale/selector";
import { transactionsSelector } from "src/store/transaction/selector";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { tryYmdStringToEndOfDayUtc } from "src/utils/timezone";

function normalizeName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/, " ");
}

const useStyles = makeStyles({
  content: {
    "&:first-child": {
      paddingTop: 0,
    },
  },
});

interface ContentProps {
  onClose: (bankAccount?: { id: string; name: string }) => void;
  bankAccount: Omit<
    ReturnType<typeof bankAccountsSelector>[number],
    "user" | "generalLedgerAccount" | "integration"
  >;
}

export function getNameFromIntegration(integration: any) {
  if (!integration) return "";
  switch (integration.type) {
    case "cash":
    case "expense":
      return integration.account_name;
    case "ebay":
      return "eBay";
    default:
      return capitalize(integration.type);
  }
}

function Content({ bankAccount, onClose }: ContentProps) {
  const recordEvent = useRecordEvent();

  const history = useHistory();
  const store = useStore();
  const integrations = useSelector(nonAssociatedIntegrationsSelector);
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const classes = useStyles();
  const [dialog, setDialog] = useState<{ type: "confirm" } | null>(null);
  const formik = useFormik({
    initialValues: {
      subType: bankAccount.integration_id ? "integrated" : "manual",
      ...bankAccount,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (bankAccount.id && bankAccount.integration_id) {
        const integration = integrationByIdSelector(
          store.getState(),
          bankAccount.integration_id
        );

        if (integration)
          errors["integration_id"] = `The ${getNameFromIntegration(
            integration
          )} integration is associated with this account. To assign a new integration to this account, you need to delete the ${getNameFromIntegration(
            integration
          )} integration.`;
      }

      if (values.subType === "integrated") {
        if (!values.integration_id) errors.integration_id = "Required field";
      } else if (!values.name) errors.name = "Required field";

      if (!values.id && values.name) {
        const normalizedName = normalizeName(values.name);
        const accounts = generalLedgerAccountsSelector(store.getState());
        const hasExistingGlName = accounts.some((account) => {
          if (account.deleted || !account.name) return false;
          return normalizeName(account.name) === normalizedName;
        });

        if (hasExistingGlName) {
          errors.name =
            "This name is already used by a general ledger account. Please choose a different name.";
        }
      }
      return errors;
    },
    onSubmit: async (values) => {
      if (values.id) {
        if (values.subType === "integrated") {
          setDialog({
            type: "confirm",
          });
          return;
        }

        const db = firestore();
        await db.collection("Bank_Accounts").doc(values.id).update({
          type: formik.values.type,
        });
        recordEvent({
          type: "feature-adoption",
          feature: "integration-added",
          element: "bank-integration",
        });
        toast.success("Bank Account saved successfully.");
        onClose({
          id: values.id,
          name: values.name,
        });
        return;
      }
      const acounts = generalLedgerAccountsSelector(store.getState());
      const glAccount =
        acounts.find(
          (a) =>
            a.name === values.name &&
            a.type[0] === "assets" &&
            a.type[1] === "bank-account-cash"
        ) ||
        (await addGeneralLedgerAccount(
          values.name,
          values.type === "credit-card"
            ? ["liabilities", "credit-card"]
            : ["assets", "bank-account-cash"]
        ));
      const bankAccount: Omit<BankAccount, "id" | "user"> = {
        ...(values.integration_id
          ? {
              integration_id: values.integration_id,
            }
          : undefined),
        name: values.name,
        general_ledger_account_id: glAccount.id,
        type: values.type,
      };
      const created = await addBankAccount(bankAccount);
      toast.success("Bank Account saved successfully.");
      onClose(created);
    },
    validateOnBlur: false,
    validateOnChange: false,
  });
  const update = !!formik.values.id;
  const initialIntegration = useSelector((state) =>
    update
      ? integrationByIdSelector(state, bankAccount.integration_id)
      : undefined
  );

  const integration = useSelector((state) =>
    update
      ? integrationByIdSelector(state, formik.values.integration_id)
      : undefined
  );

  const isIntegrated = formik.values.subType === "integrated";
  const canChange = !update || !initialIntegration;

  return (
    <>
      <DialogTitle>{`${
        formik.values.id ? "Edit" : "Add"
      } Account`}</DialogTitle>
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <DialogContent className={classes.content}>
          <TextField
            disabled={formik.isSubmitting || !canChange || update}
            select
            label="Account Type"
            variant="outlined"
            fullWidth
            value={formik.values.type}
            name="type"
            onChange={(ev) => {
              const type = ev.target.value as BankAccountType;
              formik.setValues((v) => {
                const values = { ...v };
                if (type !== values.type) {
                  if (update) values.name = bankAccount.name;
                  else values.name = "";
                  delete values.integration_id;
                }
                return {
                  ...values,
                  type,
                };
              });
            }}
            margin="dense"
          >
            <MenuItem value="sale-platform">Sale Platform</MenuItem>
            <MenuItem value="bank-account">
              {update ? "Bank Account / Credit Card" : "Bank Account"}
            </MenuItem>
            <MenuItem value="credit-card">
              {update ? "Bank Account / Credit Card" : "Credit Card"}
            </MenuItem>
          </TextField>
          <TextField
            select
            disabled={formik.isSubmitting || !canChange}
            margin="dense"
            label="Account Sub-Type"
            variant="outlined"
            fullWidth
            name="subType"
            onChange={(ev) => {
              const subType = ev.target.value;
              formik.setValues((v) => {
                const values = { ...v };
                if (subType !== values.subType) {
                  if (update) values.name = bankAccount.name;
                  else values.name = "";
                  delete values.integration_id;
                }
                return {
                  ...values,
                  subType,
                };
              });
            }}
            value={formik.values.subType}
          >
            <MenuItem value="integrated">Integrated</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </TextField>
          {isIntegrated ? (
            <TextField
              select
              required
              disabled={formik.isSubmitting || !canChange}
              margin="dense"
              label="Integration"
              variant="outlined"
              fullWidth
              name="integration_id"
              error={!!formik.errors.integration_id}
              helperText={formik.errors.integration_id}
              onChange={(ev) => {
                const integrationId = ev.target.value;
                if (integrationId === "add-account") {
                  history.push("/integrations");
                  return;
                }
                const integration = integrations.find(
                  (i) => i.id === integrationId
                );
                if (!integration) return;
                formik.setValues((v) => ({
                  ...v,
                  integration_id: integrationId,
                  name: getNameFromIntegration(integration),
                }));
              }}
              value={formik.values.integration_id || ""}
            >
              <MenuItem value="add-account">Add Account</MenuItem>
              {initialIntegration ? (
                <MenuItem
                  key={initialIntegration.id}
                  value={initialIntegration.id}
                >
                  {`${capitalize(initialIntegration.type)} - ${
                    (initialIntegration as any).account_name ||
                    (initialIntegration as any).name ||
                    (initialIntegration as any).username
                  }`}
                </MenuItem>
              ) : null}
              {integrations.map((integration: any) =>
                (
                  formik.values.type === "bank-account" ||
                  formik.values.type === "credit-card"
                    ? integration.type === "cash" ||
                      integration.type === "expense"
                    : integration.type !== "cash" &&
                      integration.type !== "expense"
                ) ? (
                  <MenuItem
                    key={integration.id}
                    value={integration.id}
                  >{`${capitalize(integration.type)} - ${
                    integration.account_name ||
                    integration.name ||
                    integration.username
                  }`}</MenuItem>
                ) : null
              )}
            </TextField>
          ) : null}
          <TextField
            required
            disabled={formik.isSubmitting || update}
            inputProps={{ readOnly: isIntegrated }}
            margin="dense"
            variant="outlined"
            fullWidth
            label="Name"
            onChange={formik.handleChange}
            name="name"
            value={formik.values.name}
            error={!!formik.errors.name}
            helperText={formik.errors.name}
          />
          {!canChange ? (
            <Box mt={1}>
              <Alert severity="info">
                The {getNameFromIntegration(initialIntegration)} integration is
                associated with this account. To assign a new integration to
                this account, you need to delete the{" "}
                {getNameFromIntegration(initialIntegration)} integration.
              </Alert>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <LoadableButton
            color="primary"
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
            disabled={!canChange}
          >
            {formik.values.id ? "Update" : "Add"}
          </LoadableButton>
        </DialogActions>
      </form>
      <ConfirmDialog
        open={dialog?.type === "confirm"}
        title="Are you sure?"
        text={`Attaching the ${getNameFromIntegration(
          integration
        )} integration to this bank account will update all the sales and/or transactions from the ${getNameFromIntegration(
          integration
        )} integration to be labelled as ${getNameFromIntegration(
          integration
        )} in the "Sale Platform" and "Bank Account" fields of sales and transactions respectively. The integration name will also be updated to ${getNameFromIntegration(
          integration
        )}. Are you sure you want to connect this integration to this bank account?`}
        onCancel={() => {
          setDialog(null);
        }}
        onConfirm={async () => {
          const db = firestore();
          const oldName = bankAccount.name;
          const newName = formik.values.name;
          const state = store.getState();
          const batch = new FirebaseBatcher();

          let date = new Date();
          const sales = getAllSales(state);
          for (const sale of sales) {
            if (sale.sale_platform !== oldName) continue;
            await batch.update(db.collection("Sales").doc(sale.id), {
              sale_platform: newName,
            });

            const d = sale.is_return ? sale.return_date : sale.sale_date;
            if (d && d < date) date = d;
          }

          const transactions = transactionsSelector(state);
          for (const transaction of transactions) {
            if (transaction.integration_name !== oldName) continue;
            await batch.update(
              db.collection("Transactions").doc(transaction.id),
              {
                integration_name: newName,
              }
            );

            if (transaction.date && transaction.date < date)
              date = transaction.date;
          }

          for (let i = 1; i < bankAccount.reconciliations.length; i++) {
            const reconciliation = bankAccount.reconciliations[i];
            const reconciliationDate = tryYmdStringToEndOfDayUtc(
              reconciliation.date,
              timeZone
            );
            if (reconciliationDate && date <= reconciliationDate) {
              await batch.update(
                db.collection("Reconciliations").doc(reconciliation.id),
                {
                  open: true,
                }
              );
            }
          }

          const acounts = generalLedgerAccountsSelector(state);
          const glAccount =
            acounts.find(
              (a) =>
                a.name === formik.values.name &&
                a.type[0] === "assets" &&
                a.type[1] === "bank-account-cash"
            ) ||
            (await addGeneralLedgerAccount(
              formik.values.name,
              formik.values.type === "credit-card"
                ? ["liabilities", "credit-card"]
                : ["assets", "bank-account-cash"]
            ));
          await batch.update(
            db.collection("Bank_Accounts").doc(bankAccount.id),
            {
              integration_id: formik.values.integration_id
                ? formik.values.integration_id
                : firebase.firestore.FieldValue.delete(),
              name: formik.values.name,
              general_ledger_account_id: glAccount.id,
              type: formik.values.type,
            }
          );

          await batch.commit();

          setDialog(null);
          toast.success("Bank Account saved successfully.");
          onClose();
        }}
      />
    </>
  );
}

interface AddBankAccountDialogProps {
  open: boolean;
  id?: string;
  onClose: ContentProps["onClose"];
  type?: BankAccountType;
}

const EmptySalePlatform = {
  id: "",
  type: "sale-platform" as const,
  general_ledger_account_id: "",
  name: "",
  reconciliations: [],
};

const EmptyBankAccount = {
  ...EmptySalePlatform,
  type: "bank-account" as const,
};

function Wrapper({ open, ...props }: AddBankAccountDialogProps) {
  const propsRef = useRef(props);
  if (open) propsRef.current = props;

  const bankAccount =
    useSelector((state) =>
      propsRef.current.id
        ? bankAccountByIdSelector(state, propsRef.current.id)
        : undefined
    ) ||
    (propsRef.current.type === "sale-platform"
      ? EmptySalePlatform
      : EmptyBankAccount);

  return <Content {...propsRef.current} bankAccount={bankAccount} />;
}

function AddBankAccountDialog({
  open,
  type,
  onClose,
  id,
}: AddBankAccountDialogProps) {
  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <Wrapper open={open} type={type} id={id} onClose={onClose} />
    </Dialog>
  );
}

export default AddBankAccountDialog;
